import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiAlertOctagon, FiCheck, FiClock, FiTrash2, FiMessageSquare, FiAlertCircle, FiChevronDown, FiChevronUp, FiRefreshCw, FiImage } from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

const BugReportsPanel = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedReport, setExpandedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState({});

  const fetchReports = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('bug_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReports(data || []);
    } catch (error) {
      console.error('Error fetching bug reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();

    // Real-time subscription
    const channel = supabase
      .channel('bug_reports_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bug_reports'
        },
        (payload) => {
          console.log('Bug report change:', payload);
          if (payload.eventType === 'INSERT') {
            setReports(prev => [payload.new, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setReports(prev => prev.map(r => r.id === payload.new.id ? payload.new : r));
          } else if (payload.eventType === 'DELETE') {
            setReports(prev => prev.filter(r => r.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };
      
      if (newStatus === 'resolved') {
        updateData.resolved_at = new Date().toISOString();
      }

      const { error } = await supabase
        .from('bug_reports')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const updatePriority = async (id, newPriority) => {
    try {
      const { error } = await supabase
        .from('bug_reports')
        .update({ priority: newPriority, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error updating priority:', error);
    }
  };

  const saveAdminNotes = async (id) => {
    try {
      const { error } = await supabase
        .from('bug_reports')
        .update({ admin_notes: adminNotes[id], updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error saving notes:', error);
    }
  };

  const deleteReport = async (id) => {
    if (!confirm('Delete this bug report?')) return;
    
    try {
      const { error } = await supabase
        .from('bug_reports')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting report:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'in_progress': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'resolved': return 'bg-green-500/20 text-green-400 border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-400';
      case 'medium': return 'text-yellow-400';
      case 'low': return 'text-green-400';
      default: return 'text-gray-400';
    }
  };

  const stats = {
    total: reports.length,
    open: reports.filter(r => r.status === 'open').length,
    inProgress: reports.filter(r => r.status === 'in_progress').length,
    resolved: reports.filter(r => r.status === 'resolved').length
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-2xl font-bold text-white">{stats.total}</div>
          <div className="text-sm text-gray-400">Total</div>
        </div>
        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
          <div className="text-2xl font-bold text-red-400">{stats.open}</div>
          <div className="text-sm text-red-400/70">Open</div>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
          <div className="text-2xl font-bold text-yellow-400">{stats.inProgress}</div>
          <div className="text-sm text-yellow-400/70">In Progress</div>
        </div>
        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
          <div className="text-2xl font-bold text-green-400">{stats.resolved}</div>
          <div className="text-sm text-green-400/70">Resolved</div>
        </div>
      </div>

      {/* Header with refresh */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <FiAlertOctagon className="text-orange-400" />
          Bug Reports ({reports.length})
        </h3>
        <button
          onClick={fetchReports}
          className="p-2 rounded-lg hover:bg-gray-800 transition-colors"
          disabled={loading}
        >
          <FiRefreshCw className={`w-5 h-5 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto" />
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FiAlertOctagon className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No bugs reported</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {reports.map((report) => (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden"
              >
                {/* Header */}
                <div
                  className="p-4 cursor-pointer hover:bg-gray-800/70 transition-colors"
                  onClick={() => setExpandedReport(expandedReport === report.id ? null : report.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs rounded-full border ${getStatusColor(report.status)}`}>
                          {report.status === 'open' ? 'Open' : report.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                        </span>
                        <span className={`text-xs font-medium ${getPriorityColor(report.priority)}`}>
                          <FiAlertCircle className="inline w-3 h-3 mr-1" />
                          {report.priority?.toUpperCase()}
                        </span>
                        {report.status === 'open' && (
                          <span className="flex items-center gap-1 text-xs text-red-400 animate-pulse">
                            <span className="w-2 h-2 bg-red-500 rounded-full" />
                            NOUVEAU
                          </span>
                        )}
                      </div>
                      <h4 className="font-medium text-white">{report.title}</h4>
                      <p className="text-sm text-gray-400 mt-1 line-clamp-1">{report.description}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className="text-xs text-gray-500">
                        {new Date(report.created_at).toLocaleDateString('fr-FR')}
                      </span>
                      {expandedReport === report.id ? (
                        <FiChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Content */}
                <AnimatePresence>
                  {expandedReport === report.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-gray-700"
                    >
                      <div className="p-4 space-y-4">
                        {/* User Info */}
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <span className="ml-2 text-white">{report.user_email || 'N/A'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Page:</span>
                            <span className="ml-2 text-cyan-400 text-xs break-all">{report.page_url}</span>
                          </div>
                        </div>

                        {/* Full Description */}
                        <div>
                          <span className="text-sm text-gray-500">Full Description:</span>
                          <p className="mt-1 text-white bg-gray-900/50 rounded-lg p-3 text-sm">
                            {report.description}
                          </p>
                        </div>

                        {/* Screenshots */}
                        {report.screenshots && report.screenshots.length > 0 && (
                          <div>
                            <span className="text-sm text-gray-500 flex items-center gap-2 mb-2">
                              <FiImage className="w-4 h-4" />
                              Screenshots ({report.screenshots.length})
                            </span>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {report.screenshots.map((url, idx) => (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="relative group aspect-video rounded-lg overflow-hidden border border-gray-700 hover:border-purple-500 transition-colors"
                                >
                                  <img 
                                    src={url} 
                                    alt={`Screenshot ${idx + 1}`}
                                    className="w-full h-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <span className="text-white text-xs">Click to open</span>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Browser Info */}
                        <div>
                          <span className="text-sm text-gray-500">Browser:</span>
                          <p className="mt-1 text-gray-400 text-xs break-all">{report.browser_info}</p>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-700">
                          {/* Status Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateStatus(report.id, 'open')}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                report.status === 'open' 
                                  ? 'bg-red-500 text-white' 
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              Open
                            </button>
                            <button
                              onClick={() => updateStatus(report.id, 'in_progress')}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                report.status === 'in_progress' 
                                  ? 'bg-yellow-500 text-black' 
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <FiClock className="inline w-3 h-3 mr-1" />
                              In Progress
                            </button>
                            <button
                              onClick={() => updateStatus(report.id, 'resolved')}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                report.status === 'resolved' 
                                  ? 'bg-green-500 text-black' 
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <FiCheck className="inline w-3 h-3 mr-1" />
                              Resolved
                            </button>
                          </div>

                          {/* Priority Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => updatePriority(report.id, 'low')}
                              className={`px-2 py-1 rounded text-xs ${
                                report.priority === 'low' ? 'bg-green-500/20 text-green-400' : 'bg-gray-800 text-gray-500'
                              }`}
                            >
                              Low
                            </button>
                            <button
                              onClick={() => updatePriority(report.id, 'medium')}
                              className={`px-2 py-1 rounded text-xs ${
                                report.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-gray-800 text-gray-500'
                              }`}
                            >
                              Medium
                            </button>
                            <button
                              onClick={() => updatePriority(report.id, 'high')}
                              className={`px-2 py-1 rounded text-xs ${
                                report.priority === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-gray-800 text-gray-500'
                              }`}
                            >
                              High
                            </button>
                          </div>

                          <button
                            onClick={() => deleteReport(report.id)}
                            className="ml-auto px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
                          >
                            <FiTrash2 className="inline w-3 h-3 mr-1" />
                            Delete
                          </button>
                        </div>

                        {/* Admin Notes */}
                        <div className="pt-4 border-t border-gray-700">
                          <label className="text-sm text-gray-500 flex items-center gap-2 mb-2">
                            <FiMessageSquare className="w-4 h-4" />
                            Admin Notes
                          </label>
                          <textarea
                            value={adminNotes[report.id] ?? report.admin_notes ?? ''}
                            onChange={(e) => setAdminNotes({ ...adminNotes, [report.id]: e.target.value })}
                            placeholder="Add notes..."
                            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                            rows={2}
                          />
                          <button
                            onClick={() => saveAdminNotes(report.id)}
                            className="mt-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-all"
                          >
                            Save Notes
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default BugReportsPanel;
